import { useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import {
    Button,
    Modal,
    ModalComponent,
    ModalProps,
    SelectField,
} from '@speeki/global-ui-components';
import { notify } from '@speeki/global-ui-components';

import { StandardForm } from '@components/atoms/StandardForm/StandardForm';

import {
    Country_Code,
    UpdateSettingsDocument,
} from '@graphql/generated/graphql';

import { countriesOptions, defaultSelectOption } from '@utils/dictionary';
import { useUserOptions } from '@utils/hooks/useUserOptions';

import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { requiredSelectV2 } from '@shared/validations';

interface CountryChangeModalProps
    extends Pick<ModalProps, 'onClose' | 'open'> {}

export const CountryChangeModal = ({
    onClose,
    open,
}: CountryChangeModalProps) => {
    const { t } = useTranslation();
    const { data } = useUserOptions();

    const {
        control,
        formState: { errors, isDirty, isSubmitting },
        handleSubmit,
        reset,
    } = useForm<ChangeCountryFormValues>({
        defaultValues: {
            country: defaultSelectOption(
                countriesOptions(),
                data?.user.country,
            ),
        },
        mode: 'onChange',
        resolver: yupResolver(ChangeCountrySchema),
    });

    const [updateSettings, { loading }] = useMutation(UpdateSettingsDocument, {
        context: {
            replaceTokens: true,
        },
        onCompleted: () => {
            reset();
            onClose();
            notify(t('CHANGE_COUNTRY_SUCCESS')).SUCCESS();
        },
        onError: () => {},
    });

    const handleCancel = () => {
        reset();
        onClose();
    };

    const handleCountryChange = (data: ChangeCountryFormValues) => {
        void updateSettings({
            variables: {
                input: { country: data.country.value as Country_Code },
            },
        });
    };

    const { field: countrySelect } = useController({
        control,
        name: 'country',
    });

    return (
        <ModalComponent closeButton onClose={onClose} open={open} width={648}>
            <Modal.Content variant="secondary">
                <Modal.Header>
                    <Modal.TitleSecondary center>
                        {t('CHANGE_COMPANY_TEXT')}
                    </Modal.TitleSecondary>
                    <Modal.Description>
                        {t('SETTINGS_CHANGE_COUNTRY_TEXT')}
                    </Modal.Description>
                </Modal.Header>

                <StandardForm>
                    <SelectField
                        {...countrySelect}
                        error={!!errors.country}
                        label={t('COUNTRY')}
                        message={t(errors.country?.message || '')}
                        options={countriesOptions()}
                        placeholder={t('SELECT_COUNTRY')}
                        testId="select_change-country"
                    />
                </StandardForm>
            </Modal.Content>

            <Modal.Footer>
                <Modal.ButtonsWrapper>
                    <Button
                        onClick={handleCancel}
                        testId="button_cancel"
                        variant={'secondary'}
                    >
                        {t('CANCEL')}
                    </Button>
                    <Button
                        disabled={!isDirty || isSubmitting}
                        isLoading={isSubmitting || loading}
                        onClick={handleSubmit(handleCountryChange)}
                        testId="button_save"
                        variant={'primary'}
                    >
                        {t('SAVE')}
                    </Button>
                </Modal.ButtonsWrapper>
            </Modal.Footer>
        </ModalComponent>
    );
};

const ChangeCountrySchema = yup.object({
    country: requiredSelectV2,
});

type ChangeCountryFormValues = yup.InferType<typeof ChangeCountrySchema>;
